// ============================================================================
// FILE: src/modules/ticket/pages/sla/SlaDashboardPage.js
// UPDATED: 2025-12-14
// PURPOSE: SLA Dashboard Page (Multilingual + Clean UI + No Import Issues)
// ============================================================================
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import axiosClient from "../../../../api/axiosClient";
import { AuthContext } from "../../../../context/AuthContext";

import SLAChartCategory from "../../components/sla/SLAChartCategory";
import SLAChartEscalation from "../../components/sla/SLAChartEscalation";
import SLAChartTrend from "../../components/sla/SLAChartTrend";
import SLAViolationsTable from "../../components/sla/SLAViolationsTable";

// ---------------------------------------------------------------------------
// KPI CARD
// ---------------------------------------------------------------------------
function KpiCard({ label, value, unit }) {
  return (
    <div
      className="p-3 rounded-4 shadow-sm bg-white"
      style={{ flex: 1, minWidth: 210 }}
    >
      <div
        style={{
          fontSize: 13,
          opacity: 0.8,
          marginBottom: 6,
        }}
      >
        {label}
      </div>

      <div style={{ fontSize: 28, fontWeight: 700 }}>
        {value}{" "}
        {unit && <small style={{ fontSize: 14, opacity: 0.7 }}>{unit}</small>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN PAGE
// ---------------------------------------------------------------------------
export default function SlaDashboardPage() {
  const { t } = useTranslation();
  const { activeScope } = useContext(AuthContext);

  const tenant_id = activeScope?.tenant_id;
  const location_id = activeScope?.location_id;

  const [loading, setLoading] = useState(false);

  const [kpi, setKpi] = useState({
    sla_breach_percentage: 0,
    mttr_hours: 0,
    mtta_hours: 0,
  });

  const [category, setCategory] = useState([]);
  const [escalation, setEscalation] = useState({});
  const [trend, setTrend] = useState([]);
  const [violations, setViolations] = useState([]);

  // -------------------------------------------------------------------------
  // API: Load Dashboard
  // -------------------------------------------------------------------------
  async function loadDashboard() {
    if (!tenant_id || !location_id) return;

    setLoading(true);

    try {
      const res = await axiosClient.get("/ticket-management/sla/dashboard", {
        params: {
          tenant_id,
          location_id,
          limit: 20,
          offset: 0,
        },
      });

      const data = res?.data?.data;
      setKpi(data.kpi);
      setCategory(data.charts.sla_by_category);
      setEscalation(data.charts.escalation_levels);
      setTrend(data.charts.trend);
      setViolations(data.violations);
    } catch (error) {
      console.error("SLA Dashboard load error:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line
  }, [tenant_id, location_id]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (!tenant_id || !location_id) {
    return (
      <div className="container py-5 text-danger">
        {t("common.scope_missing", "Tenant / Location missing")}
      </div>
    );
  }

  return (
    <div className="tf-page-wrapper">
      <div className="tf-page-container container-fluid px-4">

        {/* ----------------------------------------------------------------- */}
        {/* HEADER */}
        {/* ----------------------------------------------------------------- */}
        <div
          className="mb-4 px-4 py-3 text-white d-flex justify-content-between align-items-center"
          style={{
            borderRadius: "18px",
            background: "linear-gradient(90deg,#6A00F4 0%, #5200CC 100%)",
          }}
        >
          <div>
            <h3 className="mb-0 fw-bold">{t("sla.dashboard.header")}</h3>
            <small className="text-light">
              {t("sla.dashboard.subtitle")}
            </small>
          </div>

          <div>
            <button
              className="btn btn-light btn-sm fw-semibold"
              onClick={loadDashboard}
              disabled={loading}
            >
              {loading ? t("common.loading", "Loading...") : t("sla.dashboard.refresh")}
            </button>
          </div>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* KPI CARDS */}
        {/* ----------------------------------------------------------------- */}
        <div className="d-flex flex-wrap gap-3 mb-4">
          <KpiCard
            label={t("sla.kpi.breach")}
            value={kpi.sla_breach_percentage}
            unit="%"
          />

          <KpiCard
            label={t("sla.kpi.mttr")}
            value={kpi.mttr_hours ?? "-"}
            unit={t("sla.kpi.mttr_unit")}
          />

          <KpiCard
            label={t("sla.kpi.mtta")}
            value={kpi.mtta_hours ?? "-"}
            unit={t("sla.kpi.mtta_unit")}
          />
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* CHARTS ROW */}
        {/* ----------------------------------------------------------------- */}
        <div className="row mb-4">
          <div className="col-md-6 mb-4">
            <SLAChartCategory
              title={t("sla.charts.category")}
              data={category}
            />
          </div>

          <div className="col-md-6 mb-4">
            <SLAChartEscalation
              title={t("sla.charts.escalation")}
              data={escalation}
            />
          </div>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* TREND */}
        {/* ----------------------------------------------------------------- */}
        <div className="mb-4">
          <SLAChartTrend
            title={t("sla.charts.trend")}
            data={trend}
          />
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* VIOLATIONS TABLE */}
        {/* ----------------------------------------------------------------- */}
        <div className="mb-5">
          <SLAViolationsTable
            title={t("sla.table.violations")}
            data={violations}
          />
        </div>

      </div>
    </div>
  );
}
